import React, { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff, Info } from "lucide-react";
import AuthPageShell, { AuthFormCard } from "../../components/ecommerce/AuthPageShell";
import SignUpStepProgress from "../../components/ecommerce/SignUpStepProgress";
import { EC } from "../../utils/const/ecommerce-colors-const";
import { useProcessing, useToast } from "../../utils/context/ui-feedback-context";
import { FormSubmitButton } from "../../components/form-actions-component";
import { verificarNombreYNitEmpresa, validarNombreUsuario } from "../../api/api-sign-up";
import { registrarEmpresa } from "../../api/api-sign-up";

export default function SignUpPage() {
    const { t } = useTranslation();
    const history = useHistory();
    const [formData, setFormData] = useState({
        codigoEmpresa: "",
        nombreEmpresa: "",
        nitEmpresa: "",
        telefonoEmpresa: "",
        emailEmpresa: "",
        usuario: "",
        password: "",
        confirmPassword: "",
    });
    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [passwordError, setPasswordError] = useState("");
    const [telefonoEmpresaInfoOpen, setTelefonoEmpresaInfoOpen] = useState(false);
    const telefonoEmpresaInfoRef = useRef(null);

    useEffect(() => {
        if (!telefonoEmpresaInfoOpen) return undefined;
        const handleClickOutside = (event) => {
            if (
                telefonoEmpresaInfoRef.current &&
                !telefonoEmpresaInfoRef.current.contains(event.target)
            ) {
                setTelefonoEmpresaInfoOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [telefonoEmpresaInfoOpen]);

    // Toast only (success / error messages)
    const { showToast } = useToast();
    // Processing overlay only (loading while API runs)
    const { showProcessing, hideProcessing } = useProcessing();

    const [sendRequest, setSendRequest] = useState(false);

    const charLimits = {
        codigoEmpresa: 128,
        nombreEmpresa: 256,
        nitEmpresa: 45,
        telefonoEmpresa: 128,
        emailEmpresa: 256,
        usuario: 64,
        password: 32,
        confirmPassword: 32,
    };

    useEffect(() => {
        console.log("SignUp");
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        const limit = charLimits[name];
        if (limit == null || value.length <= limit) {
            setFormData((prev) => ({ ...prev, [name]: value }));
        }
    };

    const validarStep1 = async () => {
        try {
            let message = "";
            if (formData.nombreEmpresa === undefined || formData.nombreEmpresa === null || formData.nombreEmpresa.trim().length === 0) {
                message += t("alert_debe_ingresar_nombre_empresa") + "\n";
            }
            if (message.length > 0) {
                return { error: true, message: message };
            }
            return { error: false };
        } catch (error) {
            console.log("validarStep1 error", error);
            return { error: true, message: error };
        }
    };

    const handleNextStep = async (e) => {
        try {
            e.preventDefault();
            // console.log("handleNextStep");
            const isValid = await validarStep1();
            if (isValid.error) {
                // showToast(isValid.message);
                return;
            }
            let objEnviar = {};
            objEnviar.nombreEmpresa = String(formData.nombreEmpresa).trim();
            objEnviar.nitEmpresa = null;
            if (String(formData.nitEmpresa).trim().length > 0) {
                objEnviar.nitEmpresa = formData.nitEmpresa;
            }
            setSendRequest(true);
            showProcessing();
            const response = await verificarNombreYNitEmpresa(objEnviar);
            const total = response.body ?? 0;
            if (response.error) {
                showToast(response.message);
                return;
            }
            if (response.error === false && total > 0) {
                showToast(t("alert_empresa_o_nit_ya_se_encuentra_registrado"));
                return;
            }
            if (response.error === false && total === 0) {
                setStep(2);
            }
        } catch (err) {
            console.log("handleNextStep err", err);
        } finally {
            setSendRequest(false);
            hideProcessing();
        }
    };

    const validarStep2 = () => {
        let message = "";
        if (formData.usuario.trim().length === 0) {
            message += t("alert_debe_ingresar_nombre_usuario") + "\n";
        }
        if (formData.password.trim().length === 0) {
            message += t("alert_debe_ingresar_password") + "\n";
        }
        if (formData.confirmPassword.trim().length === 0) {
            message += t("alert_debe_ingresar_confirmar_password") + "\n";
        }
        return message.length > 0 ? { error: true, message } : { error: false };
    };

    const createAccount = async (e) => {
        e.preventDefault();
        setPasswordError("");
        const validateInputs = validarStep2();
        if (validateInputs.error) {
            showToast(validateInputs.message);
            return;
        }
        if (formData.password !== formData.confirmPassword) {
            setPasswordError(t("alert_passwords_no_coinciden"));
            return;
        }
        try {
            setLoading(true);
            showProcessing();
            const responseNombreUsuario = await validarNombreUsuario({ usuario: formData.usuario });
            // console.log("responseNombreUsuario", responseNombreUsuario);
            if (responseNombreUsuario.error) {
                showToast(responseNombreUsuario.message);
                return;
            }
            if (responseNombreUsuario.error === false && responseNombreUsuario.body > 0) {
                showToast(t("alert_nombre_usuario_ya_se_encuentra_registrado"));
                return;
            }

            if (responseNombreUsuario.error === false && responseNombreUsuario.body === 0) {
                console.log("crear usuario");
            }

            const offsetInHours = -new Date().getTimezoneOffset() / 60;
            formData.timeZone = offsetInHours;
            // await registrarEmpresa(formData);
            // console.log("formData", formData);
            // console.log("formData.timeZone", formData.timeZone);
            // console.log('registrar empresa');
            let objEnviar = {};
            objEnviar.codigoEmpresa = formData.codigoEmpresa;
            objEnviar.nombreEmpresa = formData.nombreEmpresa;
            objEnviar.nitEmpresa = formData.nitEmpresa;
            objEnviar.telefonoEmpresa = formData.telefonoEmpresa;
            objEnviar.emailEmpresa = formData.emailEmpresa;
            objEnviar.usuario = formData.usuario;
            objEnviar.password = formData.password;
            objEnviar.confirmPassword = formData.confirmPassword;
            objEnviar.timeZone = formData.timeZone;
            // console.log("objEnviar", objEnviar);
            const response = await registrarEmpresa(objEnviar);
            // console.log("response", response);
            if (response.error) {
                showToast(response.message);
                return;
            }
            if (response.error === false) {
                showToast(t("alert_empresa_registrada_correctamente"), "success", 5000);
                setTimeout(() => {
                    history.push("/login");
                }, 5000);
                return;
            }
        } catch (err) {
            console.log("createAccount err", err);
        } finally {
            setLoading(false);
            hideProcessing();
        }
    };

    const renderField = (id, label, required = false, infoText = null, infoOpen = false, setInfoOpen = null, infoRef = null) => {
        const fieldValue = formData[id] ?? "";
        return (
            <div className="mb-4">
                <div className="mb-1.5 flex items-center gap-2">
                    <label htmlFor={id} className={`${EC.form.label} mb-0`}>
                        {label}
                        {required && <span className="text-ct-price-500"> *</span>}
                    </label>
                    {infoText && setInfoOpen && (
                        <div className="relative" ref={infoRef}>
                            <button
                                type="button"
                                onClick={() => setInfoOpen(!infoOpen)}
                                className="inline-flex h-5 w-5 items-center justify-center rounded-full text-ct-primary-600 hover:bg-ct-primary-50 hover:text-ct-primary-800 transition"
                                aria-label={t("label_info")}
                                aria-expanded={infoOpen}
                            >
                                <Info size={16} />
                            </button>
                            {infoOpen && (
                                <div className="absolute left-0 z-20 mt-2 w-64 rounded-xl border border-ct-primary-100 bg-white p-3 text-sm text-slate-700 shadow-lg shadow-slate-900/10 sm:w-72">
                                    <p>{infoText}</p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <input
                    type="text"
                    id={id}
                    name={id}
                    value={fieldValue}
                    onChange={handleChange}
                    className={EC.form.input}
                    maxLength={charLimits[id]}
                    disabled={loading}
                />
                <p className={EC.form.helper}>
                    {fieldValue.length}/{charLimits[id]}
                </p>
            </div>
        );
    };

    const renderPasswordField = (id, label, show, setShow) => {
        const fieldValue = formData[id] ?? "";
        return (
            <div className="mb-4">
                <label htmlFor={id} className={EC.form.label}>
                    {label}
                    <span className="text-ct-price-500"> *</span>
                </label>
                <div className="relative">
                    <input
                        type={show ? "text" : "password"}
                        id={id}
                        name={id}
                        value={fieldValue}
                        onChange={handleChange}
                        className={`${EC.form.input} pr-12`}
                        maxLength={charLimits[id]}
                        disabled={loading}
                    />
                    <button
                        type="button"
                        onClick={() => setShow(!show)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-ct-primary-600 transition-colors"
                        aria-label={show ? "Hide password" : "Show password"}
                    >
                        {show ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                </div>
                <p className={EC.form.helper}>
                    {fieldValue.length}/{charLimits[id]}
                </p>
            </div>
        );
    };

    return (
        <AuthPageShell
            badge={t("ct_signup_title")}
            badgeIcon="✨"
            title={t("ct_brand")}
            subtitle={t("ct_signup_subtitle")}
            maxWidth="max-w-lg"
        >
            <SignUpStepProgress currentStep={step} />

            <AuthFormCard>
                {step === 1 && (
                    <form onSubmit={handleNextStep} className="space-y-2">
                        <h2 className="text-xl font-bold text-slate-900 mb-4">{t("ct_signup_step1")}</h2>
                        {renderField("nombreEmpresa", t("label_nombre"), true)}
                        {renderField("nitEmpresa", t("label_codigo"))}
                        {renderField(
                            "telefonoEmpresa",
                            t("label_telefono_empresa"),
                            false,
                            t("info_telefono_empresa_whatsapp"),
                            telefonoEmpresaInfoOpen,
                            setTelefonoEmpresaInfoOpen,
                            telefonoEmpresaInfoRef
                        )}
                        {renderField("emailEmpresa", t("label_email_address"))}

                        <FormSubmitButton
                            type="submit"
                            loading={sendRequest}
                            label={t("ct_signup_register")}
                            className={`${EC.form.submit} mt-4 w-full`}
                        />

                        <button
                            type="button"
                            onClick={() => history.push("/login")}
                            className={`${EC.form.link} w-full justify-center mt-4`}
                        >
                            {t("ct_signup_back")}
                        </button>
                    </form>
                )}

                {step === 2 && (
                    <form onSubmit={createAccount} className="space-y-2">
                        <h2 className="text-xl font-bold text-slate-900 mb-4">{t("ct_signup_step2")}</h2>
                        {renderField("usuario", t("label_usuario"), true)}
                        {renderPasswordField("password", t("label_contrasena"), showPassword, setShowPassword)}
                        {renderPasswordField("confirmPassword", t("label_confirmar_contrasena"), showConfirmPassword, setShowConfirmPassword)}

                        {passwordError && <p className={EC.form.error}>{passwordError}</p>}

                        <FormSubmitButton
                            type="submit"
                            loading={sendRequest}
                            label={t("ct_signup_register")}
                            className={`${EC.form.submit} mt-4 w-full`}
                        />

                        <button
                            type="button"
                            onClick={() => setStep(1)}
                            className={`${EC.form.link} w-full justify-center mt-4`}
                        >
                            {t("ct_signup_back")}
                        </button>
                    </form>
                )}

                <div className="pt-6 mt-6 border-t border-slate-100 text-center">
                    <p className="text-sm text-slate-600">
                        {t("ct_signup_have_account")}{" "}
                        <button
                            type="button"
                            onClick={() => history.push("/login")}
                            className="font-semibold text-ct-primary-700 hover:text-ct-primary-800"
                        >
                            {t("label_iniciar_sesion")}
                        </button>
                    </p>
                </div>
            </AuthFormCard>

            <div className="mt-6 text-center">
                <button type="button" onClick={() => history.push("/landing")} className={EC.form.link}>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    {t("label_volver_al_inicio")}
                </button>
            </div>
        </AuthPageShell>
    );
}
